import React, { useState } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('customer_inquiries')
        .insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          inquiry_type: 'contact',
          status: 'new'
        });

      if (error) throw error;

      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });

      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      alert('Failed to submit message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Let's Brighten &amp; Beautify Your Space!
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Ready to transform your home or business? Contact us today for a free consultation 
            and detailed estimate. We're here to bring your vision to life.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          
          {/* Phone */}
          <div className="text-center group">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-500 transition-colors">
              <i className="ri-phone-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Call Us Today</h3>
            <p className="text-gray-600 mb-4">Speak directly with our team</p>
            <a 
              href="tel:416-648-2980" 
              className="text-2xl font-bold text-teal-600 hover:text-teal-700 transition-colors"
            >
              416-648-2980
            </a>
            <p className="text-sm text-gray-500 mt-2">Mon-Sat: 8AM-6PM</p>
          </div>

          {/* Email */}
          <div className="text-center group">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-500 transition-colors">
              <i className="ri-mail-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Email Us</h3>
            <p className="text-gray-600 mb-4">Send us your project details</p>
            <a 
              href="mailto:Nisar.noorzai@gmail.com" 
              className="text-lg font-semibold text-teal-600 hover:text-teal-700 transition-colors break-all"
            >
              Nisar.noorzai@gmail.com
            </a>
            <p className="text-sm text-gray-500 mt-2">We respond within 24 hours</p>
          </div>

          {/* Location */}
          <div className="text-center group">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-500 transition-colors">
              <i className="ri-map-pin-line text-3xl text-teal-600 group-hover:text-white transition-colors"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Service Area</h3>
            <p className="text-gray-600 mb-4">We serve the Greater Toronto Area</p>
            <div className="text-gray-700 space-y-1">
              <p className="font-semibold">Flemingdon Park, North York</p>
              <p className="text-sm">Scarborough • Markham • Richmond Hill</p>
              <p className="text-sm">Vaughan • Mississauga • Brampton</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 placeholder-gray-400 shadow-sm placeholder-gray-500 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm rounded-md"
                  placeholder="Your Name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 placeholder-gray-400 shadow-sm placeholder-gray-500 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm rounded-md"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 placeholder-gray-400 shadow-sm placeholder-gray-500 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm rounded-md"
                  placeholder="+1 416 648 2980"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 placeholder-gray-400 shadow-sm placeholder-gray-500 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm rounded-md"
                  placeholder="Tell us about your project and we'll get back to you soon."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full px-8 py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill mr-2"></i>
                    Send Message
                  </>
                )}
              </button>

              {submitted && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-700 text-center font-semibold">
                    <i className="ri-checkbox-circle-line mr-2"></i>
                    Message sent successfully! We'll get back to you soon.
                  </p>
                </div>
              )}
            </form>
          </div>

          {/* Map Section */}
          <div className="bg-gray-50 rounded-3xl p-8 mb-16">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Find Us on the Map</h3>
              <p className="text-gray-600">Based in Flemingdon Park, serving the entire GTA</p>
            </div>
            
            <div className="relative w-full h-96 rounded-2xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d23020.123456789!2d-79.3492!3d43.7615!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89d4d1d6d1234567%3A0x123456789abcdef!2sFlemingdon%20Park%2C%20Toronto%2C%20ON!5e0!3m2!1sen!2sca!4v1234567890123!5m2!1sen!2sca"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="EB Bright Painting & Decoration Services Location"
              />
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="text-center">
          <div className="inline-flex flex-col sm:flex-row gap-4">
            <button 
              onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-black text-white rounded-lg font-medium hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Request Free Quote
            </button>
            <button 
              onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
              className="px-8 py-4 border-2 border-teal-500 text-teal-600 rounded-lg font-medium hover:bg-teal-500 hover:text-white transition-colors whitespace-nowrap"
            >
              <i className="ri-chat-3-line mr-2"></i>
              Chat with Our Team
            </button>
          </div>
          
          <p className="text-sm text-gray-500 mt-6 max-w-2xl mx-auto">
            Have questions about your project? Our experienced team is ready to help you choose 
            the best solutions for your painting, flooring, and insulation needs.
          </p>
        </div>
      </div>
    </section>
  );
};
